import { Card, Stack } from '@mui/material';
import {
    CreateButton,
    ListToolbar,
    Pagination,
    SortButton,
    Title,
    TopToolbar,
} from 'react-admin';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AnimationId } from '@wwf/sdk';
import { useAnimationDrawer } from '@app/animation/drawer/AnimationDrawer';
import { AnimationListContent } from './AnimationListContent';
import { AnimationListFilter } from './AnimationListFilter';

export const AnimationListLayout = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { openDrawer } = useAnimationDrawer();

    const editIdFromUrl = searchParams.get('edit') as AnimationId | null;
    const lastHandledEditId = useRef<AnimationId | null>(null);

    if (!editIdFromUrl) {
        lastHandledEditId.current = null;
    }

    useEffect(() => {
        if (!editIdFromUrl || editIdFromUrl === lastHandledEditId.current) {
            return;
        }
        lastHandledEditId.current = editIdFromUrl;
        openDrawer('edit', editIdFromUrl);
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev);
                next.delete('edit');
                return next;
            },
            { replace: true }
        );
    }, [editIdFromUrl, openDrawer, setSearchParams]);

    return (
        <Stack direction="row">
            <AnimationListFilter sx={{ mt: 2 }} />
            <Stack sx={{ width: '100%' }}>
                <Title title={'animation'} />
                <ListToolbar
                    actions={
                        <AnimationListActions
                            openCreateDrawer={() => openDrawer('create', null)}
                        />
                    }
                />
                <Card>
                    <AnimationListContent
                        openDrawer={id => openDrawer('show', id)}
                    />
                </Card>
                <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
            </Stack>
        </Stack>
    );
};

const AnimationListActions = ({
    openCreateDrawer,
}: {
    openCreateDrawer?: () => void;
}) => (
    <TopToolbar>
        <SortButton fields={['name', 'createdAt', 'updatedAt', 'enabled']} />
        <CreateButton
            resource="animation"
            variant="contained"
            label="New Animation"
            onClick={openCreateDrawer ?? undefined}
            sx={{ marginLeft: 2 }}
        />
    </TopToolbar>
);
